import * as React from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useBookingT } from "@/hooks/use-translations"
import { getProviderConfig } from "@/features/integrations/data/providerConfigs"
import type { ConfigField } from "@/features/integrations/data/providerConfigs"
import type { Connection } from "@/features/integrations/api/types"

type Props = {
  integrationId: string
  connection: Connection | null
}

export function ConfigSection({ integrationId, connection }: Props) {
  const t = useBookingT()
  const config = getProviderConfig(integrationId)
  const [values, setValues] = React.useState<Record<string, unknown>>(connection?.config ?? {})

  React.useEffect(() => {
    setValues(connection?.config ?? {})
  }, [connection?.config])

  if (!config) {
    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-1">
            {t("integrations_detail_configuration", "Configuration")}
          </h2>
          <p className="text-sm text-neutral-600">
            {t(
              "integrations_config_default_desc",
              "This integration has no specific settings. Connect to start using it.",
            )}
          </p>
        </CardContent>
      </Card>
    )
  }

  const updateValue = (key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-6">
        <h2 className="text-lg font-semibold text-neutral-900">
          {t("integrations_detail_configuration", "Configuration")}
        </h2>

        {config.sections.map((section) => (
          <section key={section.titleKey} className="space-y-4">
            <h3 className="text-base font-medium text-neutral-900">
              {t(section.titleKey, section.titleFallback)}
            </h3>
            <div className="space-y-4">
              {section.fields.map((field) => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={values[field.key]}
                  onChange={(v) => updateValue(field.key, v)}
                  disabled={!connection}
                />
              ))}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  )
}

function FieldRenderer({
  field,
  value,
  onChange,
  disabled,
}: {
  field: ConfigField
  value: unknown
  onChange: (v: unknown) => void
  disabled?: boolean
}) {
  const t = useBookingT()
  const label = t(field.labelKey, field.labelFallback)

  if (field.kind === "text") {
    return (
      <div className="space-y-2">
        <Label htmlFor={`cfg-${field.key}`}>{label}</Label>
        <Input
          id={`cfg-${field.key}`}
          value={(value as string) ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    )
  }

  if (field.kind === "switch") {
    const description = field.descriptionKey
      ? t(field.descriptionKey, field.descriptionFallback ?? "")
      : null
    return (
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-0.5">
          <Label htmlFor={`cfg-${field.key}`} className="font-medium">
            {label}
          </Label>
          {description && <p className="text-sm text-neutral-600">{description}</p>}
        </div>
        <Switch
          id={`cfg-${field.key}`}
          checked={!!value}
          onCheckedChange={onChange}
          disabled={disabled}
        />
      </div>
    )
  }

  if (field.kind === "select") {
    return (
      <div className="space-y-2">
        <Label htmlFor={`cfg-${field.key}`}>{label}</Label>
        <Select
          value={(value as string) ?? ""}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger id={`cfg-${field.key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  if (field.kind === "info") {
    return (
      <div className="space-y-1">
        <span className="text-xs uppercase tracking-wide text-neutral-700">{label}</span>
        <p className="text-sm text-neutral-900">{(value as string) ?? "—"}</p>
      </div>
    )
  }

  return null
}
